/**
 * `components.MuiChip` — tonal variant colors and label/icon sizing.
 * Discoverability: `MuiChip`, `Chip`, `variant: 'tonal'`, semantic colors (`pending`, `upcoming`, …).
 *
 * @see https://mui.com/material-ui/api/chip/
 *
 */
import type { Components, Theme } from '@mui/material/styles';

export function muiChip(theme: Theme): Pick<Components<Theme>, 'MuiChip'> {
  return {
    MuiChip: {
      styleOverrides: {
        sizeSmall: {
          height: '22px',
          borderRadius: '6px',
        },
        sizeMedium: {
          height: '28px',
          borderRadius: '8px',
        },
        labelSmall: {
          ...theme.typography.captionRegular,
        },
        label: {
          ...theme.typography.paragraphRegular,
        },
      },
      variants: [
        {
          props: { variant: 'tonal' },
          style: {},
        },
        {
          props: { variant: 'tonal', color: 'primary' },
          style: {
            '&.MuiChip-colorPrimary': {
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.dark,
            },
            '& .MuiChip-iconColorPrimary': {
              color: theme.palette.primary.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'secondary' },
          style: {
            '&.MuiChip-colorSecondary': {
              backgroundColor: theme.palette.secondary.light,
              color: theme.palette.secondary.dark,
            },
            '& .MuiChip-iconColorSecondary': {
              color: theme.palette.secondary.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'success' },
          style: {
            '&.MuiChip-colorSuccess': {
              backgroundColor: theme.palette.success.light,
              color: theme.palette.success.dark,
            },
            '& .MuiChip-iconColorSuccess': {
              color: theme.palette.success.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'info' },
          style: {
            '&.MuiChip-colorInfo': {
              backgroundColor: theme.palette.info.light,
              color: theme.palette.info.dark,
            },
            '& .MuiChip-iconColorInfo': {
              color: theme.palette.info.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'warning' },
          style: {
            '&.MuiChip-colorWarning': {
              backgroundColor: theme.palette.warning.light,
              color: theme.palette.warning.dark,
            },
            '& .MuiChip-iconColorWarning': {
              color: theme.palette.warning.dark,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'error' },
          style: {
            '&.MuiChip-colorError': {
              backgroundColor: theme.palette.error.light,
              color: theme.palette.error.dark,
            },
            '& .MuiChip-iconColorError': {
              color: theme.palette.error.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'inactive' },
          style: {
            '&.MuiChip-colorInactive': {
              backgroundColor: theme.palette.inactive.light,
              color: theme.palette.inactive.dark,
            },
            '& .MuiChip-iconColorInactive': {
              color: theme.palette.inactive.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'active' },
          style: {
            '&.MuiChip-colorActive': {
              backgroundColor: theme.palette.active.light,
              color: theme.palette.active.dark,
            },
            '& .MuiChip-iconColorActive': {
              color: theme.palette.active.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'upcoming' },
          style: {
            '&.MuiChip-colorUpcoming': {
              backgroundColor: theme.palette.upcoming.light,
              color: theme.palette.upcoming.dark,
            },
            '& .MuiChip-iconColorUpcoming': {
              color: theme.palette.upcoming.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'critical' },
          style: {
            '&.MuiChip-colorCritical': {
              backgroundColor: theme.palette.critical.light,
              color: theme.palette.critical.dark,
            },
            '& .MuiChip-iconColorCritical': {
              color: theme.palette.critical.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'pending' },
          style: {
            '&.MuiChip-colorPending': {
              backgroundColor: theme.palette.pending.light,
              color: theme.palette.pending.dark,
            },
            '& .MuiChip-iconColorPending': {
              color: theme.palette.pending.main,
            },
          },
        },
      ],
    },
  };
}
